import React, { useEffect, useMemo, useState } from "react";
import { api, fmtINR, todayISO, fmtDate } from "../lib/api";
import { exportCSV, printPDF, fmtINRPlain } from "../lib/export";
import { openConsolidatedWindow, shareConsolidatedWhatsApp } from "../lib/bill";
import {
  Wallet,
  AlertCircle,
  MessageCircle,
  Download,
  Printer,
  Search,
  FileText,
  X,
} from "lucide-react";
import { toast } from "sonner";

const COMPANY_PHONE = "9025811576";

const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function Receivables() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [payNotes, setPayNotes] = useState("");

  // Weekly consolidated bill
  const [consCustomer, setConsCustomer] = useState("");
  const [consFrom, setConsFrom] = useState(daysAgoISO(6));
  const [consTo, setConsTo] = useState(todayISO());
  const [consData, setConsData] = useState(null);

  const load = async () => {
    const res = await api.get("/reports/receivables");
    setData(res.data);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const ql = q.trim().toLowerCase();
    const rows = data.debtors;
    if (!ql) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(ql) ||
        (r.phone || "").includes(ql)
    );
  }, [data, q]);

  const recordPayment = async () => {
    const amount = parseFloat(payAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    await api.post("/customer-payments", {
      customer_id: selected.customer_id,
      date: todayISO(),
      amount,
      mode: payMode,
      notes: payNotes,
    });
    toast.success(`Recorded ${fmtINR(amount)} from ${selected.name}`);
    setSelected(null);
    setPayAmount("");
    setPayNotes("");
    load();
  };

  const reminderWA = (r) => {
    const lines = [
      `Hello *${r.name}*,`,
      ``,
      `This is a friendly reminder from *EEMAN MEATS*.`,
      `Your outstanding balance is *${fmtINR(r.balance)}*.`,
      r.last_sale_date ? `Last order: ${r.last_sale_date}` : "",
      ``,
      `Kindly settle at your convenience. Thank you!`,
    ]
      .filter(Boolean)
      .join("\n");
    const phone = (r.phone || "").replace(/[^\d]/g, "");
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(lines)}`
      : `https://wa.me/?text=${encodeURIComponent(lines)}`;
    window.open(url, "_blank");
  };

  const generateConsolidated = async (customerId) => {
    const cid = customerId || consCustomer;
    if (!cid) {
      toast.error("Select a customer");
      return;
    }
    const res = await api.get(
      `/reports/consolidated-bill?customer_id=${cid}&from_date=${consFrom}&to_date=${consTo}`
    );
    setConsData(res.data);
    if (res.data.order_count === 0 && res.data.old_balance === 0) {
      toast.info("No sales found in this date range");
    }
  };

  const doExportCSV = () => {
    if (!data) return;
    exportCSV(
      `receivables_${todayISO()}.csv`,
      [
        { key: "name", label: "Customer" },
        { key: "phone", label: "Phone" },
        { key: "orders", label: "Orders" },
        { key: "last_sale_date", label: "Last Order" },
        { key: "billed", label: "Billed", value: (r) => Number(r.billed).toFixed(2) },
        { key: "paid_total", label: "Paid", value: (r) => Number(r.paid_total).toFixed(2) },
        { key: "balance", label: "Outstanding (INR)", value: (r) => Number(r.balance).toFixed(2) },
      ],
      data.debtors
    );
  };

  const doExportPDF = () => {
    if (!data) return;
    const rowsHtml = data.debtors
      .map(
        (r) => `<tr>
          <td>${r.name}</td>
          <td>${r.phone || "—"}</td>
          <td>${r.last_sale_date ? fmtDate(r.last_sale_date) : "—"}</td>
          <td class="right">${r.orders}</td>
          <td class="right">${fmtINRPlain(r.billed)}</td>
          <td class="right">${fmtINRPlain(r.paid_total)}</td>
          <td class="right"><b>${fmtINRPlain(r.balance)}</b></td>
        </tr>`
      )
      .join("");
    const body = `<h1>EEMAN MEATS</h1>
      <div class="sub">Receivables — Outstanding Balances · ${todayISO()}</div>
      <div class="kpi-grid">
        <div class="kpi"><div class="lbl">Total Outstanding</div><div class="val">${fmtINRPlain(data.total_outstanding)}</div></div>
        <div class="kpi"><div class="lbl">Customers Owing</div><div class="val">${data.debtor_count}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Customer</th><th>Phone</th><th>Last Order</th>
          <th class="right">Orders</th><th class="right">Billed</th>
          <th class="right">Paid</th><th class="right">Balance</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
      </table>`;
    printPDF("Receivables", body);
  };

  if (!data) {
    return (
      <div className="p-8 text-zinc-500" data-testid="receivables-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="receivables-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
            <Wallet className="h-3 w-3" /> Money Owed
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1">
            Receivables
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={doExportCSV}
            data-testid="recv-export-csv"
            className="border border-zinc-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-100 flex items-center gap-1"
          >
            <Download className="h-3 w-3" /> CSV
          </button>
          <button
            onClick={doExportPDF}
            data-testid="recv-export-pdf"
            className="bg-zinc-900 text-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-800 flex items-center gap-1"
          >
            <Printer className="h-3 w-3" /> PDF
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className="border border-rose-200 bg-rose-50 p-5" data-testid="recv-total">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-rose-800">
              Total Outstanding
            </div>
            <AlertCircle className="h-4 w-4 text-rose-600" />
          </div>
          <div className="font-display text-3xl font-bold font-mono-num mt-2 text-rose-700">
            {fmtINR(data.total_outstanding)}
          </div>
          <div className="text-xs text-rose-800 mt-1">
            {data.debtor_count} customer{data.debtor_count !== 1 ? "s" : ""} owe you
          </div>
        </div>
        <div className="border border-zinc-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Top Debtor
          </div>
          {data.debtors[0] ? (
            <>
              <div className="font-display text-xl font-bold mt-2">{data.debtors[0].name}</div>
              <div className="font-mono-num text-sm text-rose-700 mt-1">
                {fmtINR(data.debtors[0].balance)}
              </div>
            </>
          ) : (
            <div className="text-sm text-zinc-500 mt-2">— No debtors</div>
          )}
        </div>
        <div className="border border-zinc-200 bg-white p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Avg per Customer
          </div>
          <div className="font-display text-xl font-bold font-mono-num mt-2">
            {data.debtor_count > 0
              ? fmtINR(data.total_outstanding / data.debtor_count)
              : "—"}
          </div>
        </div>
      </div>

      {/* Weekly consolidated bill */}
      <div className="border-2 border-zinc-900 bg-white p-5 mb-5" data-testid="consolidated-section">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-4 w-4 text-sky-600" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Weekly Consolidated Bill (wholesale customers)
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <select
            value={consCustomer}
            onChange={(e) => setConsCustomer(e.target.value)}
            data-testid="cons-customer-select"
            className="md:col-span-5 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          >
            <option value="">Select customer…</option>
            {data.customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.name}
                {c.balance > 0 ? ` (due ${fmtINR(c.balance)})` : ""}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={consFrom}
            onChange={(e) => setConsFrom(e.target.value)}
            data-testid="cons-from-date"
            className="md:col-span-3 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          />
          <input
            type="date"
            value={consTo}
            onChange={(e) => setConsTo(e.target.value)}
            data-testid="cons-to-date"
            className="md:col-span-2 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
          />
          <button
            onClick={() => generateConsolidated()}
            data-testid="cons-generate-btn"
            className="md:col-span-2 bg-zinc-900 text-white px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-800"
          >
            Generate
          </button>
        </div>

        {consData && (
          <div className="mt-4 border border-zinc-200" data-testid="cons-preview">
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-sm font-semibold">{consData.customer_name}</div>
                <div className="text-xs text-zinc-500">
                  {fmtDate(consData.from)} → {fmtDate(consData.to)} · {consData.order_count} bills
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total Due</div>
                <div className="font-display text-2xl font-bold font-mono-num text-rose-700" data-testid="cons-grand-due">
                  {fmtINR(consData.grand_due)}
                </div>
              </div>
            </div>
            <div className="px-4 py-3 space-y-1.5 text-sm">
              {consData.orders.map((o) => (
                <div key={o.id} className="flex justify-between text-xs">
                  <span>{fmtDate(o.date)} · #{o.order_no} · {o.items.map((i) => i.product_name).join(", ")}</span>
                  <span className="font-mono-num">{fmtINR(o.total_amount)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t border-zinc-200">
                <span className="text-zinc-600">Period Total</span>
                <span className="font-mono-num font-semibold">{fmtINR(consData.period_total)}</span>
              </div>
              {consData.period_paid > 0 && (
                <div className="flex justify-between">
                  <span className="text-zinc-600">Paid</span>
                  <span className="font-mono-num text-emerald-700">− {fmtINR(consData.period_paid)}</span>
                </div>
              )}
              {consData.old_balance > 0 && (
                <div className="flex justify-between">
                  <span className="text-zinc-600">Previous Balance</span>
                  <span className="font-mono-num text-rose-600">+ {fmtINR(consData.old_balance)}</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 p-3 border-t border-zinc-200">
              <button
                onClick={() =>
                  shareConsolidatedWhatsApp(consData, {
                    phone: consData.phone,
                    companyPhone: COMPANY_PHONE,
                  })
                }
                data-testid="cons-whatsapp-btn"
                className="bg-emerald-600 text-white px-3 py-2.5 text-xs uppercase tracking-wider hover:bg-emerald-700 flex items-center justify-center gap-1"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </button>
              <button
                onClick={() => openConsolidatedWindow(consData, { companyPhone: COMPANY_PHONE })}
                data-testid="cons-print-btn"
                className="border border-zinc-900 px-3 py-2.5 text-xs uppercase tracking-wider hover:bg-zinc-100 flex items-center justify-center gap-1"
              >
                <Printer className="h-4 w-4" /> Print
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="border border-zinc-200 bg-white px-3 py-2 mb-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-zinc-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search customers…"
          data-testid="recv-search"
          className="flex-1 outline-none text-sm bg-transparent"
        />
        {q && (
          <button onClick={() => setQ("")} className="text-zinc-400 hover:text-zinc-900">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Debtors table */}
      <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="recv-table">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">
            {data.debtor_count === 0
              ? "✓ All customers are settled up"
              : "No customers match your search"}
          </div>
        ) : (
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Last Order</th>
                <th className="px-4 py-3 text-right">Billed</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3 text-right">Outstanding</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.customer_id}
                  className="border-b border-zinc-100 hover:bg-zinc-50"
                  data-testid={`debtor-${r.customer_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold">{r.name}</div>
                    {r.phone && (
                      <div className="text-xs text-zinc-500 font-mono-num">{r.phone}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-600">
                    {r.last_sale_date ? fmtDate(r.last_sale_date) : "—"}
                    <div className="text-[10px] text-zinc-400">{r.orders} order{r.orders !== 1 ? "s" : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono-num text-right">
                    {fmtINR(r.billed)}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono-num text-right">
                    {fmtINR(r.paid_total)}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono-num text-right font-bold text-rose-700">
                    {fmtINR(r.balance)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => {
                          setConsCustomer(r.customer_id);
                          generateConsolidated(r.customer_id);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        title="Generate consolidated bill"
                        data-testid={`debtor-bill-${r.customer_id}`}
                        className="border border-zinc-300 text-zinc-700 hover:bg-zinc-100 px-2 py-1 text-[10px] uppercase tracking-wider flex items-center gap-1"
                      >
                        <FileText className="h-3 w-3" /> Bill
                      </button>
                      <button
                        onClick={() => reminderWA(r)}
                        title="Send WhatsApp reminder"
                        data-testid={`debtor-wa-${r.customer_id}`}
                        className="border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2 py-1 text-[10px] uppercase tracking-wider flex items-center gap-1"
                      >
                        <MessageCircle className="h-3 w-3" /> Remind
                      </button>
                      <button
                        onClick={() => setSelected(r)}
                        data-testid={`debtor-collect-${r.customer_id}`}
                        className="bg-sky-600 text-white px-2 py-1 text-[10px] uppercase tracking-wider hover:bg-sky-700"
                      >
                        Collect
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Collect payment modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="collect-modal"
        >
          <div
            className="bg-white border border-zinc-900 max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Record payment from
                </div>
                <div className="font-display text-xl font-bold">{selected.name}</div>
                <div className="text-xs text-rose-700 font-mono-num mt-1">
                  Outstanding: {fmtINR(selected.balance)}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-zinc-400 hover:text-zinc-900">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-2">
              <input
                type="number"
                step="0.01"
                placeholder="Amount ₹"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                data-testid="collect-amount"
                className="w-full border border-zinc-300 px-3 py-2 font-mono-num text-lg focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setPayAmount(String(selected.balance.toFixed(2)))}
                  className="border border-zinc-300 px-3 py-2 text-xs uppercase tracking-wider hover:bg-zinc-100"
                >
                  Full ({fmtINR(selected.balance)})
                </button>
                <select
                  value={payMode}
                  onChange={(e) => setPayMode(e.target.value)}
                  data-testid="collect-mode"
                  className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
                >
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <input
                placeholder="Notes (optional)"
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                data-testid="collect-notes"
                className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
              />
              <button
                onClick={recordPayment}
                data-testid="collect-save"
                className="w-full bg-emerald-600 text-white py-3 font-medium uppercase tracking-wider hover:bg-emerald-700"
              >
                Record Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
